import React, { useState, useRef } from 'react';
import { Button } from '../../components/Button/Button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/DropdownMenu';
import { Icons } from '../../components/Icons/Icons';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../components/Tooltip/Tooltip';
import { cn } from '../../lib/utils';

interface DataRowProps {
  number: number | null;
  disableEditing: boolean;
  description: string;
  details?: { primary: string[]; secondary: string[] };
  isSelected?: boolean;
  onSelect?: (e) => void;
  isVisible: boolean;
  onToggleVisibility?: (e) => void;
  isLocked: boolean;
  onToggleLocked: (e) => void;
  title: string;
  onRename: (e) => void;
  onDelete: (e) => void;
  colorHex?: string;
  onColor: (e) => void;
  className?: string;
}

export const DataRow: React.FC<DataRowProps> = ({
  number,
  title,
  colorHex,
  details,
  onSelect,
  isLocked,
  onToggleVisibility,
  onToggleLocked,
  onRename,
  onDelete,
  onColor,
  isSelected = false,
  isVisible = true,
  disableEditing = false,
  className,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  const handleAction = (action: string, e: React.MouseEvent) => {
    e.stopPropagation();
    switch (action) {
      case 'Rename': onRename(e); break;
      case 'Lock': onToggleLocked(e); break;
      case 'Delete': onDelete(e); break;
      case 'Color': onColor(e); break;
    }
  };

  const decodeHTML = (html: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  const hasPrimary = details?.primary?.length > 0;
  const hasSecondary = details?.secondary?.length > 0;

  return (
    <div
      ref={rowRef}
      className={cn(
        'animate-fade-in-up mx-0.5 my-1 flex flex-col overflow-hidden rounded-lg transition-all duration-200',
        !isVisible && 'opacity-50',
        isSelected
          ? 'shadow-[0_0_0_1.5px_hsl(var(--primary)/0.6),0_4px_16px_rgba(0,0,0,0.4)]'
          : 'shadow-[0_1px_4px_rgba(0,0,0,0.25)]',
        className
      )}
      style={{
        background: isSelected
          ? 'linear-gradient(135deg, hsl(var(--accent)/0.9), hsl(var(--card)/0.95))'
          : 'hsl(var(--card))',
      }}
    >
      {/* Left accent bar */}
      <div
        className={cn(
          'flex flex-col',
          isSelected ? 'border-l-2' : 'border-l-2 border-transparent'
        )}
        style={isSelected && colorHex
          ? { borderLeftColor: colorHex }
          : isSelected
          ? { borderLeftColor: 'hsl(var(--primary))' }
          : undefined
        }
      >
        {/* Main row */}
        <div
          className="group relative flex cursor-pointer items-center gap-2 px-2 py-2"
          onClick={onSelect}
          data-cy="data-row"
        >
          {/* Number badge */}
          {number !== null && (
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold transition-all duration-200',
                isSelected
                  ? 'text-white'
                  : 'bg-white/[0.07] text-white/50'
              )}
              style={isSelected ? { background: colorHex ?? 'hsl(var(--primary))', boxShadow: `0 0 8px ${colorHex ?? 'hsl(var(--primary))'}66` } : undefined}
            >
              {number}
            </div>
          )}

          {/* Color dot */}
          {colorHex && (
            <div
              className={cn(
                'h-2 w-2 shrink-0 rounded-full transition-all duration-200',
                isSelected ? 'scale-125' : 'group-hover:scale-110'
              )}
              style={{
                backgroundColor: colorHex,
                boxShadow: isSelected ? `0 0 6px ${colorHex}` : undefined,
              }}
            />
          )}

          {/* Title */}
          <div className="min-w-0 flex-1">
            <span
              className={cn(
                'block truncate text-[12.5px] font-semibold leading-snug transition-colors duration-200',
                isSelected ? 'text-white' : 'text-white/75 group-hover:text-white/90'
              )}
            >
              {title}
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex shrink-0 items-center gap-0.5">
            {/* Visibility */}
            {onToggleVisibility && (
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  'h-6 w-6 transition-all duration-150',
                  isSelected || !isVisible
                    ? 'opacity-70 hover:opacity-100'
                    : 'opacity-0 group-hover:opacity-60 hover:!opacity-100'
                )}
                aria-label={isVisible ? 'Hide' : 'Show'}
                onClick={e => { e.stopPropagation(); onToggleVisibility(e); }}
              >
                {isVisible
                  ? <Icons.Hide className="h-4 w-4" />
                  : <Icons.Show className="h-4 w-4" />
                }
              </Button>
            )}

            {/* Lock indicator */}
            {isLocked && !disableEditing && (
              <Icons.Lock className="text-muted-foreground h-4 w-4" />
            )}

            {/* More menu */}
            {disableEditing
              ? <div className="h-6 w-6" />
              : (
                <DropdownMenu onOpenChange={open => setIsDropdownOpen(open)}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn(
                        'h-6 w-6 transition-all duration-150',
                        isSelected || isDropdownOpen
                          ? 'opacity-70 hover:opacity-100'
                          : 'opacity-0 group-hover:opacity-60 hover:!opacity-100'
                      )}
                      aria-label="Actions"
                      onClick={e => e.stopPropagation()}
                    >
                      <Icons.More className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onCloseAutoFocus={e => e.preventDefault()}>
                    <DropdownMenuItem onClick={e => handleAction('Rename', e)}>
                      <Icons.Rename className="text-foreground" />
                      <span className="pl-2">Rename</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={e => handleAction('Delete', e)}>
                      <Icons.Delete className="text-foreground" />
                      <span className="pl-2">Delete</span>
                    </DropdownMenuItem>
                    {onColor && (
                      <DropdownMenuItem onClick={e => handleAction('Color', e)}>
                        <Icons.ColorChange className="text-foreground" />
                        <span className="pl-2">Change Color</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={e => handleAction('Lock', e)}>
                      <Icons.Lock className="text-foreground" />
                      <span className="pl-2">{isLocked ? 'Unlock' : 'Lock'}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            }
          </div>
        </div>

        {/* Details chips */}
        {(hasPrimary || hasSecondary) && (
          <div className="border-t border-white/[0.05] px-3 pb-2 pt-1.5">
            {(() => {
              const allLines = [
                ...(details.primary ?? []),
                ...(details.secondary ?? []),
              ].filter(l => l && l.trim() !== '');
              const LIMIT = 4;
              const visibleLines = isDetailsExpanded ? allLines : allLines.slice(0, LIMIT);
              const overflow = allLines.length - LIMIT;
              return (
                <div className="flex flex-wrap gap-1">
                  {visibleLines.map((line, i) => {
                    const clean = decodeHTML(line).trim();
                    if (!clean) return null;
                    const isIndented = line.startsWith('  ');
                    return (
                      <span
                        key={i}
                        className={cn(
                          'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10.5px] font-medium leading-none',
                          isIndented
                            ? 'bg-white/[0.04] text-white/35'
                            : isSelected
                            ? 'bg-white/[0.12] text-white/80'
                            : 'bg-white/[0.07] text-white/55'
                        )}
                      >
                        {clean}
                      </span>
                    );
                  })}
                  {!isDetailsExpanded && overflow > 0 && (
                    <button
                      onClick={e => { e.stopPropagation(); setIsDetailsExpanded(true); }}
                      className="inline-flex items-center rounded-md bg-white/[0.08] px-1.5 py-0.5 text-[10px] font-semibold text-white/40 transition-colors hover:bg-white/[0.14] hover:text-white/70"
                    >
                      +{overflow} more
                    </button>
                  )}
                  {isDetailsExpanded && overflow > 0 && (
                    <button
                      onClick={e => { e.stopPropagation(); setIsDetailsExpanded(false); }}
                      className="inline-flex items-center rounded-md bg-white/[0.08] px-1.5 py-0.5 text-[10px] font-semibold text-white/40 transition-colors hover:bg-white/[0.14] hover:text-white/70"
                    >
                      show less
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataRow;
